import { useState } from 'react';
import {
  Button,
  Dialog,
  FormAlert,
  Input,
  Skeleton,
  formatCurrency,
  formatPercent,
  useToast,
} from '@mgdlead/ui';
import { TriangleAlert } from 'lucide-react';

import { useCampaignBudgets, useUpdateBudget, type CampaignBudget } from '../../lib/api';
import { humanizeError } from '../../lib/errors';
import { useWorkspace } from '../workspace/WorkspaceContext';

/**
 * Ajustar o orçamento diário NA META, de dentro do MGDLead (pedido do dono, 06/10).
 *
 * Dois toques de propósito: "Salvar" vira "Confirmar R$ X → R$ Y". É a primeira tela que
 * gasta dinheiro do cliente; um toque distraído não pode bastar. E a variação acima de 20%
 * avisa da fase de aprendizado ANTES de confirmar, não depois.
 */
export function AjustarOrcamentoDialog({
  aberto,
  onClose,
  nomeDoBloco,
  externalIds,
}: {
  aberto: boolean;
  onClose: () => void;
  nomeDoBloco: string;
  externalIds: string[];
}) {
  const { workspace } = useWorkspace();
  const budgets = useCampaignBudgets(workspace.id, externalIds, aberto);

  return (
    <Dialog
      open={aberto}
      onOpenChange={(o) => (o ? null : onClose())}
      title={`Orçamento diário · ${nomeDoBloco}`}
      description="A mudança vai direto para a Meta e vale a partir de agora. Fica registrado quem mudou e de quanto para quanto."
      size="lg"
    >
      {budgets.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : budgets.isError ? (
        <FormAlert>{humanizeError(budgets.error)}</FormAlert>
      ) : (
        <div className="flex flex-col gap-5">
          {(budgets.data ?? []).map((c) => (
            <Campanha key={c.externalId} c={c} />
          ))}
        </div>
      )}
    </Dialog>
  );
}

function Campanha({ c }: { c: CampaignBudget }) {
  const moeda = c.moeda ?? 'BRL';
  return (
    <section className="flex flex-col gap-3">
      <div>
        <p className="font-medium text-foreground">{c.name ?? c.externalId}</p>
        <p className="text-xs text-muted-foreground">
          {c.modo === 'CBO'
            ? 'Orçamento na campanha'
            : c.modo === 'ABO'
              ? 'Orçamento em cada conjunto ativo'
              : 'Sem orçamento diário'}
          {c.gastoHojeCentavos !== null
            ? ` · gasto hoje ${formatCurrency(c.gastoHojeCentavos / 100, moeda)}`
            : ''}
        </p>
      </div>
      {!c.podeAlterar && c.motivo ? <FormAlert tone="info">{c.motivo}</FormAlert> : null}
      {c.podeAlterar
        ? c.itens.map((i) => (
            <Item
              key={i.externalId}
              externalId={i.externalId}
              nome={c.modo === 'ABO' ? i.name : null}
              atual={i.dailyBudgetCents}
              moeda={moeda}
            />
          ))
        : null}
    </section>
  );
}

/** "250", "250,50", "1.250,50" → centavos. `null` = não é número. */
function paraCentavos(texto: string): number | null {
  const limpo = texto.replace(/[^\d,.]/g, '');
  if (limpo === '') return null;
  const normal = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  const n = Number.parseFloat(normal);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function Item({
  externalId,
  nome,
  atual,
  moeda,
}: {
  externalId: string;
  nome: string | null;
  atual: number;
  moeda: string;
}) {
  const { workspace } = useWorkspace();
  const update = useUpdateBudget();
  const { toast } = useToast();
  const [texto, setTexto] = useState((atual / 100).toFixed(2).replace('.', ','));
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const novo = paraCentavos(texto);
  const mudou = novo !== null && novo !== atual;
  const variacao = novo === null ? null : (novo - atual) / atual;
  const grande = variacao !== null && Math.abs(variacao) > 0.2;

  const salvar = () => {
    if (!mudou || novo === null) return;
    if (!confirmando) {
      setConfirmando(true);
      return;
    }
    setErro(null);
    update.mutate(
      { workspaceId: workspace.id, externalId, dailyBudgetCents: novo },
      {
        onSuccess: (r) => {
          setConfirmando(false);
          toast({
            title: 'Orçamento alterado na Meta',
            description: `${formatCurrency(r.antes / 100, moeda)} → ${formatCurrency(r.depois / 100, moeda)} por dia`,
            tone: 'success',
          });
        },
        onError: (e) => {
          setConfirmando(false);
          setErro(humanizeError(e));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      {nome ? <p className="text-sm text-foreground">{nome}</p> : null}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Por dia (hoje: {formatCurrency(atual / 100, moeda)})
          <Input
            inputMode="decimal"
            value={texto}
            aria-label="Novo orçamento diário"
            onChange={(e) => {
              setTexto(e.target.value);
              setConfirmando(false);
            }}
            className="w-36"
          />
        </label>
        {mudou && variacao !== null ? (
          <span
            className={
              grande
                ? 'pb-2 text-sm font-medium text-warning'
                : 'pb-2 text-sm text-muted-foreground'
            }
          >
            {variacao >= 0 ? '+' : ''}
            {formatPercent(variacao, 0)}
          </span>
        ) : null}
        <Button
          variant={confirmando ? 'primary' : 'secondary'}
          disabled={!mudou || (novo ?? 0) < 100}
          loading={update.isPending}
          loadingLabel="Alterando na Meta"
          onClick={salvar}
        >
          {confirmando && novo !== null
            ? `Confirmar ${formatCurrency(atual / 100, moeda)} → ${formatCurrency(novo / 100, moeda)}`
            : 'Salvar'}
        </Button>
      </div>
      {grande ? (
        <p className="inline-flex items-start gap-1.5 text-xs text-warning">
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          Mudança acima de 20% de uma vez costuma reiniciar a fase de aprendizado da Meta. Se puder,
          suba aos poucos.
        </p>
      ) : null}
      {erro ? <FormAlert>{erro}</FormAlert> : null}
    </div>
  );
}
